// Entry point for the build script in your package.json
import "@hotwired/turbo-rails"
import "./controllers"
import "./channels"

// Custom Turbo Stream Actions
// These extend Turbo Streams with additional actions beyond the default 8
// Used for operations that need to bypass data-turbo-permanent restrictions
import "./turbo_stream_actions/update_badge"
import { installWizardPrefetchGuard } from "./lib/wizard_prefetch_guard"
import { installTapPrefetch } from "./lib/tap_prefetch"
import { takeListScroll, restoreListScroll } from "./lib/list_scroll"
import { releaseDetachedHolders, onOverlayChange, closeTopOverlay } from "./lib/scroll_lock"
import { NativeBridge } from "./lib/native_bridge"
import { installWizardTransitions } from "./lib/wizard_transition"
import { viewTransitionsEnabled } from "./lib/motion"

installWizardPrefetchGuard()
installTapPrefetch()
installWizardTransitions()

// Native shells animate page transitions themselves; the web page fade is skipped there.
const isTurboNative = navigator.userAgent.includes("Turbo Native")
const isAndroidNative = isTurboNative && /Android/.test(navigator.userAgent)
if (isTurboNative) {
  document.documentElement.classList.add('turbo-native')
}

window.isInitialPageLoad = true
// Disable Stimulus component-level entrance animations to avoid flicker in Turbo navigations
window.disableComponentTransitions = true

// Android WebView janks on page/list entrance animations: motion-lite skips those (lib/motion.js), while
// feedback motion (toasts, cues, Lottie) still plays. Reduced motion follows only the user's OS setting.
window.motionLite = isAndroidNative
document.documentElement.classList.toggle('motion-lite', isAndroidNative)
const prefersReducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
const applyReducedMotion = (matches) => { window.prefersReducedMotion = matches }
applyReducedMotion(prefersReducedMotionQuery.matches)

if (prefersReducedMotionQuery.addEventListener) {
  prefersReducedMotionQuery.addEventListener('change', (event) => applyReducedMotion(event.matches))
} else if (prefersReducedMotionQuery.addListener) {
  // Safari < 14 fallback
  prefersReducedMotionQuery.addListener((event) => applyReducedMotion(event.matches))
}

// Instant Swap + Soft Fade: content swaps instantly, subtle opacity fade (80ms)
const PAGE_ENTER_CLASS = 'page-entering'
const SOFT_FADE_DURATION = 80
let pageEnterTimeoutId = null

const clearPageEnter = () => {
  if (pageEnterTimeoutId) {
    clearTimeout(pageEnterTimeoutId)
    pageEnterTimeoutId = null
  }
  document.body.classList.remove(PAGE_ENTER_CLASS)
}

const runPageEnter = () => {
  if (isTurboNative || window.prefersReducedMotion || window.isInitialPageLoad || viewTransitionsEnabled()) return
  const body = document.body

  body.classList.remove(PAGE_ENTER_CLASS)
  // Force reflow so class re-apply restarts the animation on rapid navigations
  void body.offsetWidth
  body.classList.add(PAGE_ENTER_CLASS)

  pageEnterTimeoutId = setTimeout(clearPageEnter, SOFT_FADE_DURATION + 20)
}

document.addEventListener('turbo:load', () => {
  if (window.isInitialPageLoad) {
    window.isInitialPageLoad = false
    clearPageEnter()
    return
  }
  runPageEnter()
})

document.addEventListener('turbo:before-cache', clearPageEnter)

// 뒤로가기/앞으로가기 감지 (Turbo 공식 API)
// turbo:visit 이벤트의 action이 'restore'면 브라우저 히스토리 탐색
document.addEventListener('turbo:visit', (event) => {
  window._lastVisitAction = event.detail.action
})

// Scroll after a Turbo render:
//   - back/forward (restore) keeps Turbo's restored position; a product list first puts the opened card back where
//     it sat on screen (products-index#saveScroll, else the click-time offset, which beats Turbo's throttled
//     record). The saved entry is consumed by the next render either way, so it never resurfaces later.
//   - a re-render of the same URL (form error, redirect back, refresh morph) keeps the position; scroll-focus
//     moves to the first error.
//   - chat pages scroll themselves; every other visit starts at the top (wizard steps included).
document.addEventListener('turbo:render', () => {
  const saved = takeListScroll()
  if (window._lastVisitAction === 'restore') {
    if (saved !== null) requestAnimationFrame(() => restoreListScroll(saved))
    return
  }
  if (location.href === window._lastLoadedUrl) return
  if (document.querySelector('[data-controller~="chat"]')) return
  window.scrollTo(0, 0)
})

document.addEventListener('turbo:load', () => {
  window._lastLoadedUrl = location.href
  releaseDetachedHolders()
  window._lastVisitAction = null
})

// Native shells: system back closes the open overlay (window.__toas2tCloseOverlay), told via navigation.overlay.
if (NativeBridge.isNative()) {
  onOverlayChange((open) => NativeBridge.Navigation.overlay(open))
  window.__toas2tCloseOverlay = closeTopOverlay
}

// A full page load is a fresh look at the list: drop any position saved for it.
takeListScroll()

// Auto-register scroll-focus controller on all forms for keyboard-aware scrolling
// This ensures input fields scroll above mobile keyboard when focused
document.addEventListener('turbo:load', () => {
  document.querySelectorAll('form:not([data-controller~="scroll-focus"])').forEach(form => {
    // A wizard wrapper already runs one; a second instance would double every keyboard/error scroll.
    if (form.parentElement?.closest('[data-controller~="scroll-focus"]')) return
    const existingController = form.dataset.controller || ''
    form.dataset.controller = `${existingController} scroll-focus`.trim()
  })
})

